import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  ArrowBack as ArrowBackIcon,
  DeleteSweep as DeleteSweepIcon,
  Folder as FolderIcon,
  VideoFile as VideoFileIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  MonitorHeart as MonitorHeartIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import TrashService from '../../services/TrashService';
import PathSelectorDialog from '../dialogs/PathSelectorDialog';
import AdminPageWrapper from '../admin/AdminPageWrapper';
import Swal from 'sweetalert2';
import { trashBinHelpItems } from '../../utils/helpItems';
import {PageHeader} from '../common/PageHeader';

const TrashBinPage = ({ darkMode, toggleDarkMode, themeVariant }) => {
  const navigate = useNavigate();
  const [deletedItems, setDeletedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [pathSelectorOpen, setPathSelectorOpen] = useState(false);
  const [itemToRestore, setItemToRestore] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadDeletedItems();
  }, []);

  const loadDeletedItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await TrashService.getRecentlyDeletedItems();
      setDeletedItems(items);
    } catch (err) {
      console.error('Error loading deleted items:', err);
      setError('Failed to load deleted items. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRestoreClick = (item) => {
    if (item.isFolder) {
      // For folders, show path selector
      setItemToRestore(item);
      setPathSelectorOpen(true);
    } else {
      // For files, restore to original location
      handleRestoreItem(item.id);
    }
  };

  const handleRestoreItem = async (itemId, newPath = null) => {
    setRestoreLoading(true);
    try {
      await TrashService.restoreDeletedItem(itemId, newPath);
      setSuccess(`Item restored successfully${newPath ? ' to ' + newPath : ''}.`);
      
      // Remove the item from the list
      setDeletedItems(prevItems => prevItems.filter(item => item.id !== itemId));
      
      // Close dialogs
      setPathSelectorOpen(false);
      
      // Show success message
      Swal.fire({
        title: 'Success',
        text: `Item has been restored${newPath ? ' to ' + newPath : ''}.`,
        icon: 'success',
        confirmButtonText: 'OK'
      });
    } catch (err) {
      console.error('Error restoring item:', err);
      setError('Failed to restore item: ' + (err.response?.data?.message || err.message));
      
      Swal.fire({
        title: 'Error',
        text: 'Failed to restore item: ' + (err.response?.data?.message || err.message),
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setRestoreLoading(false);
      setItemToRestore(null);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    
    setDeleteLoading(true);
    try {
      await TrashService.permanentlyDeleteItem(itemToDelete.id);
      
      // Remove the item from the list
      setDeletedItems(prevItems => prevItems.filter(item => item.id !== itemToDelete.id));
      
      // Close dialog
      setConfirmDeleteOpen(false);
      setItemToDelete(null);
      
      // Show success message
      setSuccess('Item permanently deleted.');
      
      Swal.fire({
        title: 'Success',
        text: 'Item has been permanently deleted.',
        icon: 'success',
        confirmButtonText: 'OK'
      });
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Failed to delete item: ' + (err.response?.data?.message || err.message));
      
      Swal.fire({
        title: 'Error',
        text: 'Failed to delete item: ' + (err.response?.data?.message || err.message),
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setDeleteLoading(false);
    }
  };
  
  const handlePathSelected = (path) => {
    if (itemToRestore) {
      handleRestoreItem(itemToRestore.id, path);
    }
  };

  const formatDeletedDate = (date) => {
    if (!date) return 'Unknown';
    return (
      <Tooltip title={format(new Date(date), 'PPpp')}>
        <span>{formatDistanceToNow(new Date(date), { addSuffix: true })}</span>
      </Tooltip>
    );
  };

  return (
     <Box sx={{
                width: '100%',
                backgroundColor: darkMode ? 'background.default' : '#f5f5f5',
                minHeight: '100vh',
            }}>
      <Paper sx={{
        borderRadius: 0,
        padding: 0
      }}>
      <PageHeader 
        darkMode={darkMode} 
        toggleDarkMode={toggleDarkMode}
        showLogout={true}
        themeVariant={themeVariant} 
      />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 , padding: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            Home
          </Button>
          {/* Add arrow icon between buttons */}
          <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
          <Button
            variant="text"
            color="secondary"
            startIcon={<MonitorHeartIcon />}
            sx={{ mr: 2 }}
          >
            System
          </Button>
          <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
          <Button
            variant="text"
            color="secondary"
            startIcon={<DeleteIcon />}
            sx={{ mr: 2 }}
          >
            Trash
          </Button>
        </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
        <Box>
          
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={loadDeletedItems}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6">
            Items will be automatically deleted after 30 days
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can restore items to their original location or a new location, or permanently delete them.
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : deletedItems.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No deleted items found
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Original Location</TableCell>
                    <TableCell>Deleted By</TableCell>
                    <TableCell>Deleted On</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deletedItems
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item) => (
                      <TableRow
                        hover
                        key={item.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell component="th" scope="row">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {item.isFolder ? (
                              <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                            ) : (
                              <VideoFileIcon sx={{ mr: 1, color: 'secondary.main' }} />
                            )}
                            <Typography variant="body2">{item.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={item.isFolder ? 'Folder' : 'Video'} 
                            size="small"
                            sx={{
                              color: item.isFolder ? 'primary.main' : 'secondary.main'
                            }}
                            variant="outlined" 
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={item.path || ''}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {item.path || '(Not available)'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon sx={{ mr: 0.5, fontSize: '1rem', color: 'text.secondary' }} />
                            <Typography variant="body2">{item.deletedBy || 'Unknown'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ScheduleIcon sx={{ mr: 0.5, fontSize: '1rem', color: 'text.secondary' }} />
                            <Typography variant="body2">{formatDeletedDate(item.deletedAt)}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Restore">
                            <IconButton
                              onClick={() => handleRestoreClick(item)}
                              color="primary"
                              disabled={restoreLoading}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Permanently">
                            <IconButton
                              onClick={() => handleDeleteClick(item)}
                              color="error"
                              disabled={deleteLoading}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={deletedItems.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Permanently Delete Item?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to permanently delete "{itemToDelete?.name}"? This action cannot be undone.
            {itemToDelete?.isFolder && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="warning">
                  This is a folder. All contents within this folder will also be permanently deleted.
                </Alert>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            autoFocus
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Permanently Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Path Selector for Restore */}
      <PathSelectorDialog
        open={pathSelectorOpen}
        onClose={() => {
          setPathSelectorOpen(false);
          setItemToRestore(null);
        }}
        onSelect={handlePathSelected}
        title="Select Restore Location"
        confirmButtonText="Restore Here"
        loading={restoreLoading}
      />
    </Paper>
    </Box>
  );
};

export default TrashBinPage;